import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Manufacturer } from '../../services/api/domains/manufacturers/types';
import {
  modalStyles as styles,
  modalContentStyleFullHeight,
  modalBodyStyleFullHeight,
} from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import NamesTab from './fields/NamesTab';
import DetailsTab from './manufacturer/DetailsTab';
import StatusSection from './manufacturer/StatusSection';
import ProductsPanel from './manufacturer/ProductsPanel';
import FacilitiesPanel from './manufacturer/FacilitiesPanel';
import HistoryTab from './manufacturer/HistoryTab';
import CorporateStructureTab from './manufacturer/CorporateStructureTab';
import ManufacturerPrecedenceOrderTooltip from './manufacturer/ManufacturerPrecedenceOrderTooltip';
import {
  toModalFields,
  getAlwaysDisabledFields,
} from '../../domains/manufacturers/adapters';
import {
  useManufacturerNameVariants,
  useSetManufacturerPreferredName,
  useClearManufacturerPreferredName,
} from '../../services/api/domains/manufacturers/hooks';
import {
  groupVariantsByName,
  resolvePreviewSource,
} from '../../domains/manufacturers/utils/resolvePreviewSource';
import LgmButton from '../LgmButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { GRAY_200, GRAY_500 } from 'theme/grayScale';
import { Edit, Save, X } from 'lucide-react';

type ManufacturerModalTab = 'names' | 'details' | 'history' | 'structure';

const TAB_STYLE: React.CSSProperties = {
  textTransform: 'none',
  minHeight: '32px',
  padding: '6px 12px',
};

/**
 * Details-tab section titles (issue #653, Task 16 QA follow-up) — order and
 * style copied verbatim from ProductModal.tsx's own `renderSections`
 * (Associations/External Sources/Status/Metadata), not just conceptually
 * similar. `idx` only controls whether the top margin is skipped for the
 * first section, matching the product modal exactly.
 */
function detailsSectionTitleStyle(idx: number): React.CSSProperties {
  return {
    fontSize: '13px',
    fontWeight: 600,
    color: GRAY_500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: `${idx === 0 ? 0 : 20}px 0 8px 0`,
    paddingBottom: '6px',
    borderBottom: `1px solid ${GRAY_200}`,
  };
}

interface ManufacturerModalProps {
  manufacturer: Manufacturer;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedManufacturer: Manufacturer) => void;
  close: () => void;
}

const ManufacturerModal: React.FC<ManufacturerModalProps> = ({
  manufacturer,
  initialMode = 'VIEW',
  save,
  close,
}) => {
  const [activeTab, setActiveTab] = useState<ManufacturerModalTab>('names');

  // Use the hook for all modal logic
  const {
    editedEntity: editedManufacturer,
    setEditedEntity: setEditedManufacturer,
    modalMode,
    hasUnsavedChanges,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getPrimaryActionText,
    isSaving,
  } = useEditableModal({
    entity: manufacturer,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: getAlwaysDisabledFields(),
    styles,
  });

  // Preferred-name state lives here, not in NamesTab, for the same reason
  // ProductModal computes `headerResolvedName`/`headerSourcePreview` at the
  // modal level (issue #653, Task 16 QA follow-up): both the header's
  // "Sourced from" line and the Names tab body need the SAME resolved
  // name/source, and NamesTab is a sibling of the header under this
  // component, not an ancestor of it.
  //
  // `groupedOptions` is deliberately frozen after the initial fetch and
  // NEVER recomputed from a post-mutation refetch — that refetch is exactly
  // what caused the Names tab list to visibly reorder after every
  // selection (a manual-override row's rank tier moves it to a different
  // position in the raw backend array). The product modal's own synonym
  // list never has this problem because `preferred_product_name` is a
  // plain scalar the client already knows the instant it changes; nothing
  // about "who's currently selected" ever requires re-fetching or re-
  // ranking the synonym list itself.
  //
  // committed*/pending* split (issue #653, Task 16 QA follow-up — "names
  // save on close without the Save button"): unlike every other field here,
  // the preferred name isn't a column on `editedManufacturer` — it lives in
  // a separate table behind its own PUT/DELETE endpoint, so it was
  // previously written immediately on every radio-select/blur, bypassing
  // this modal's own Save-gated/discard-on-close contract entirely.
  // `committed*` is the last-known-saved server state (seeded once from the
  // initial fetch, updated again only after a successful Save); `pending*`
  // is the in-progress, uncommitted UI choice — writes now happen ONLY in
  // `handleModalSave`, and `handleModalClose` resets pending back to
  // committed, so an edit genuinely discards on Close like every other
  // field.
  const manufacturerId = editedManufacturer.manufacturer_id;
  const variantsQuery = useManufacturerNameVariants(manufacturerId);
  const setPreferredName = useSetManufacturerPreferredName();
  const clearPreferredNameMutation = useClearManufacturerPreferredName();
  const [committedHasManualOverride, setCommittedHasManualOverride] =
    useState(false);
  const [committedManualOverrideText, setCommittedManualOverrideText] =
    useState('');
  const [pendingHasManualOverride, setPendingHasManualOverride] =
    useState(false);
  const [pendingManualOverrideText, setPendingManualOverrideText] =
    useState('');
  const [namesPending, setNamesPending] = useState(false);
  const [namesError, setNamesError] = useState<string | null>(null);
  const hasInitializedOverride = useRef(false);
  const loadedVariants =
    variantsQuery.status === 'success' ? variantsQuery.data : undefined;

  useEffect(() => {
    if (hasInitializedOverride.current) return;
    if (!loadedVariants) return;
    const manualRow = loadedVariants.find(
      v => v.name_role === 'short_display' && v.source_system === 'Manual'
    );
    const initialHasOverride = !!manualRow;
    const initialText = manualRow?.name ?? '';
    setCommittedHasManualOverride(initialHasOverride);
    setCommittedManualOverrideText(initialText);
    setPendingHasManualOverride(initialHasOverride);
    setPendingManualOverrideText(initialText);
    hasInitializedOverride.current = true;
  }, [loadedVariants]);

  const groupedOptions = useMemo(
    () => groupVariantsByName(loadedVariants ?? []),
    [loadedVariants]
  );

  // The header and Names-tab list reflect the user's PENDING choice (not
  // yet saved) — mirrors ProductModal's own header, which resolves from
  // `editedProduct.preferred_product_name` (the local draft), not a
  // separately-tracked "last saved" value.
  const preview = resolvePreviewSource(
    pendingHasManualOverride,
    pendingManualOverrideText,
    groupedOptions,
    editedManufacturer.manufacturer_name
  );

  const hasPendingNamesChange =
    pendingHasManualOverride !== committedHasManualOverride ||
    (pendingHasManualOverride &&
      pendingManualOverrideText.trim() !== committedManualOverrideText.trim());

  // Local draft buffer for the Names-tab input — mirrors what the shared
  // NamesTab component expects from a plain controlled `value`/`onChange`
  // pair (issue #653, Task 16 QA follow-up: extracted the Names-tab UI into
  // that shared component, which owns no state of its own). Synced from
  // the PENDING override state whenever the input isn't focused —
  // `focusedField` already tracks focus at this level, so no separate "is
  // this input focused" flag is needed.
  const NAMES_FIELD_ID = 'manufacturer-preferred-name';
  const isNamesInputFocused = focusedField === NAMES_FIELD_ID;
  const [namesDraftText, setNamesDraftText] = useState(
    pendingManualOverrideText
  );

  useEffect(() => {
    if (!isNamesInputFocused) {
      setNamesDraftText(
        pendingHasManualOverride ? pendingManualOverrideText : ''
      );
    }
  }, [
    pendingHasManualOverride,
    pendingManualOverrideText,
    isNamesInputFocused,
  ]);

  // Performs the actual write(s) — called ONLY from `handleModalSave`, never
  // from a Names-tab interaction directly. Throws on failure so the caller
  // knows not to proceed with the rest of the save.
  const commitPendingNamesChange = async () => {
    setNamesError(null);
    setNamesPending(true);
    try {
      if (pendingHasManualOverride) {
        const trimmed = pendingManualOverrideText.trim();
        await setPreferredName.mutate({ manufacturerId, name: trimmed });
        setCommittedManualOverrideText(trimmed);
        setCommittedHasManualOverride(true);
        setPendingManualOverrideText(trimmed);
      } else {
        await clearPreferredNameMutation.mutate(manufacturerId);
        setCommittedManualOverrideText('');
        setCommittedHasManualOverride(false);
        // Refetch rather than trust the frozen groupedOptions here:
        // freezing is what prevents the list from visibly reshuffling
        // WHILE the user is comparing/selecting among options, but
        // clearing has no such "currently selected among competitors"
        // state to protect — and the frozen array's non-Manual ordering
        // isn't reliably "true precedence order minus Manual" (its
        // relative order can already have been reshuffled by whichever
        // source was Manual at fetch time). Only a fresh fetch gives the
        // actual server-computed precedence winner.
        await variantsQuery.refetch();
      }
    } catch (err) {
      setNamesError(
        err instanceof Error ? err.message : 'Failed to save preferred name'
      );
      throw err;
    } finally {
      setNamesPending(false);
    }
  };

  const combinedHasUnsavedChanges = hasUnsavedChanges || hasPendingNamesChange;
  const [isFinalizingSave, setIsFinalizingSave] = useState(false);

  const handleModalSave = async () => {
    if (modalMode === 'VIEW') {
      handleSave(); // hook: toggles VIEW -> EDIT, nothing to persist yet
      return;
    }
    if (namesPending || isFinalizingSave || !combinedHasUnsavedChanges) return;

    setIsFinalizingSave(true);
    try {
      if (hasPendingNamesChange) {
        await commitPendingNamesChange();
      }
      // Call the parent's save callback directly rather than the hook's own
      // handleSave -- that one only fires onSave when `editedManufacturer`
      // itself differs from the original, which a names-only edit never
      // does (the preferred name isn't a column on Manufacturer, so
      // committing it above never touches `editedManufacturer`). This is
      // what actually closes the modal and refreshes the manufacturers
      // grid (ManufacturerReview.tsx's handleSaveManufacturer), matching
      // what clicking Save already does for the product modal.
      //
      // `manufacturer_name` is blanked here deliberately: it's never
      // user-editable in this modal (superseded by the Names tab), so
      // `editedManufacturer.manufacturer_name` is always the STALE value
      // from when the modal opened. api/sql/manufacturers.js's
      // updateManufacturer() treats any truthy `manufacturer_name` in the
      // PUT payload as "re-set the Manual override to this text" (legacy
      // behavior predating the dedicated preferred-name endpoint) — sending
      // the stale value would silently re-apply/resurrect whatever override
      // was active at fetch time, undoing the clear/select just committed
      // above. Blank skips that path entirely; the row's real
      // manufacturer_name column already reflects the fresh precedence
      // computation via the same DB trigger the preferred-name endpoint
      // itself relies on.
      await save({ ...editedManufacturer, manufacturer_name: '' });
    } catch (err) {
      console.error('❌ Manufacturer save failed:', err);
    } finally {
      setIsFinalizingSave(false);
    }
  };

  const handleModalClose = () => {
    // The hook's own Cancel/Close confirm only knows about
    // `editedManufacturer` — extend it here for a names-only edit so that
    // case isn't silently discarded with no warning either.
    if (
      modalMode === 'EDIT' &&
      !hasUnsavedChanges &&
      hasPendingNamesChange &&
      !window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?'
      )
    ) {
      return;
    }
    setPendingHasManualOverride(committedHasManualOverride);
    setPendingManualOverrideText(committedManualOverrideText);
    handleClose();
  };

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={modalContentStyleFullHeight}>
        <div style={{ ...styles.modalHeaderStyle, marginBottom: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <h2 style={{ ...styles.modalTitleStyle, marginBottom: 0 }}>
              {variantsQuery.status === 'loading' ||
              variantsQuery.status === 'idle' ? (
                <em>Loading…</em>
              ) : (
                preview.name || <em>Not set</em>
              )}
            </h2>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: GRAY_500,
              }}>
              <span>Sourced from:</span>
              <span style={{ fontWeight: 500 }}>
                {variantsQuery.status === 'loading' ||
                variantsQuery.status === 'idle'
                  ? 'Loading…'
                  : preview.label}
              </span>
              <ManufacturerPrecedenceOrderTooltip size={13} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LgmButton
              variant="flat"
              color={modalMode === 'VIEW' ? 'warning' : 'save'}
              onClick={handleModalSave}
              disabled={
                isSaving ||
                isFinalizingSave ||
                namesPending ||
                (modalMode === 'EDIT' && !combinedHasUnsavedChanges)
              }
              style={styles.modalHeaderIconTextButtonStyle}
              aria-label={getPrimaryActionText()}
              title={getPrimaryActionText()}>
              {modalMode === 'VIEW' ? <Edit size={16} /> : <Save size={16} />}
              {getPrimaryActionText()}
            </LgmButton>
            <LgmButton
              variant="flat"
              onClick={handleModalClose}
              disabled={isSaving || isFinalizingSave}
              style={styles.modalCloseXStyle}
              hoverStyle={styles.modalCloseXHoverStyle}
              pressedStyle={styles.modalCloseXPressedStyle}
              aria-label="Close">
              <X size={16} />
            </LgmButton>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onChange={(_event, newValue: ManufacturerModalTab) =>
            setActiveTab(newValue)
          }
          style={{
            minHeight: '32px',
            borderBottom: `1px solid ${GRAY_200}`,
            padding: 0,
          }}>
          <Tab label="Names" value="names" style={TAB_STYLE} />
          <Tab label="Details" value="details" style={TAB_STYLE} />
          <Tab label="History" value="history" style={TAB_STYLE} />
          <Tab
            label="Corporate Structure"
            value="structure"
            style={TAB_STYLE}
          />
        </Tabs>

        <div style={{ ...modalBodyStyleFullHeight, marginTop: '12px' }}>
          {activeTab === 'names' ? (
            <NamesTab
              inputLabel="LGM Preferred Name"
              hintText={
                <>
                  Sets the LGM preferred name shown across the application. If
                  left blank, the name is derived using the precedence order.{' '}
                  <ManufacturerPrecedenceOrderTooltip size={12} />
                </>
              }
              value={namesDraftText}
              onChange={text => {
                setNamesDraftText(text);
                if (!text.trim()) {
                  setPendingHasManualOverride(false);
                  setPendingManualOverrideText('');
                }
              }}
              onFocus={() => setFocusedField(NAMES_FIELD_ID)}
              onBlur={() => {
                setFocusedField(null);
                const trimmed = namesDraftText.trim();
                if (trimmed) {
                  setPendingManualOverrideText(trimmed);
                  setPendingHasManualOverride(true);
                }
              }}
              isDisabled={namesPending || modalMode !== 'EDIT'}
              styles={styles}
              getFloatingLabelStyle={getFloatingLabelStyle}
              getFloatingInputStyle={getFloatingInputStyle}
              fieldId={NAMES_FIELD_ID}
              variantsLabel="Name Variants"
              variantsHintText="Select a variant to use as the Manufacturer Name, or type a custom name above."
              groups={groupedOptions}
              selectedGroupKey={preview.group?.key ?? null}
              onSelectGroup={group => {
                if (group) {
                  setPendingManualOverrideText(group.name);
                  setPendingHasManualOverride(true);
                } else {
                  setPendingHasManualOverride(false);
                  setPendingManualOverrideText('');
                }
              }}
              loadState={variantsQuery.status}
              pending={namesPending}
              bannerError={namesError}
              selectedBadgeText="Currently displayed"
            />
          ) : activeTab === 'history' ? (
            <HistoryTab manufacturerId={editedManufacturer.manufacturer_id} />
          ) : activeTab === 'structure' ? (
            <CorporateStructureTab
              manufacturerId={editedManufacturer.manufacturer_id}
            />
          ) : (
            <>
              <div>
                <h3 style={detailsSectionTitleStyle(0)}>Products</h3>
                <ProductsPanel
                  products={editedManufacturer.products}
                  onChange={products =>
                    setEditedManufacturer({ ...editedManufacturer, products })
                  }
                  isEditMode={modalMode === 'EDIT'}
                />
              </div>

              <div>
                <h3 style={detailsSectionTitleStyle(1)}>External Sources</h3>
                <DetailsTab
                  manufacturerId={editedManufacturer.manufacturer_id}
                />
              </div>

              <div>
                <h3 style={detailsSectionTitleStyle(2)}>Facilities</h3>
                <FacilitiesPanel
                  facilities={editedManufacturer.facilities}
                  onChange={facilities =>
                    setEditedManufacturer({
                      ...editedManufacturer,
                      facilities,
                    })
                  }
                  isEditMode={modalMode === 'EDIT'}
                />
              </div>

              <div>
                <h3 style={detailsSectionTitleStyle(3)}>Status</h3>
                <StatusSection
                  manufacturer={editedManufacturer}
                  setManufacturer={setEditedManufacturer}
                  isEditMode={modalMode === 'EDIT'}
                />
              </div>

              <div>
                <h3 style={detailsSectionTitleStyle(4)}>Metadata</h3>
                <ModalFieldRenderer
                  fields={toModalFields()}
                  entity={editedManufacturer}
                  setEntity={setEditedManufacturer}
                  mode={modalMode}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  isFieldDisabled={isFieldDisabled}
                  getFloatingLabelStyle={getFloatingLabelStyle}
                  getFloatingInputStyle={getFloatingInputStyle}
                  styles={styles}
                  missingMessage="Unknown"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ManufacturerModal);
export type { ModalMode };
